<template>
  <v-container class="edit-user mx-2" fluid>
    <v-breadcrumbs class="edit-user__breadcrumbs px-0 pt-0 pb-2" :items="items" />
    <div class="edit-user__header">
      <v-icon color="primary" class="edit-user__header-icon">mdi-account-edit</v-icon>
      <h1 class="edit-user__title">{{ $route.meta.title }}</h1>
    </div>
    <v-card class="edit-user__card" flat outlined>
      <v-card-text class="pa-4">
        <v-form ref="form" v-model="valid" lazy-validation>
          <h2 class="edit-user__section-title mb-3">
            <v-icon left small>mdi-information-outline</v-icon>
            Данные пользователя
          </h2>
          <v-row>
            <v-col cols="12" sm="6" class="edit-user__field-col">
              <v-text-field
                v-model="name"
                label="Имя"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-account-outline"
                class="edit-user__field"
                :rules="[() => !!name || 'Поле обязательно для заполнения']"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" class="edit-user__field-col">
              <v-text-field
                v-model="email"
                label="Email"
                outlined
                dense
                flat
                prepend-inner-icon="mdi-email-outline"
                class="edit-user__field"
                :rules="[() => /.+@.+\..+/.test(email) || 'E-mail должен быть корректным']"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" class="edit-user__field-col">
              <v-autocomplete
                v-model="idRole"
                label="Тип роли"
                :items="typesRole"
                item-text="name"
                item-value="value"
                outlined
                dense
                flat
                hide-details
                readonly
                prepend-inner-icon="mdi-account-key-outline"
                class="edit-user__field"
              />
            </v-col>
            <v-col cols="12" sm="6" class="edit-user__field-col">
              <v-text-field
                v-model="telephone"
                label="Телефон"
                type="tel"
                hint="Будет использоваться для тестовых привязок мобильного приложения"
                persistent-hint
                outlined
                dense
                flat
                prepend-inner-icon="mdi-phone-outline"
                class="edit-user__field"
                @input="telephone = formatPhoneNumberTextField(telephone || '')"
                :maxlength="(telephone || '').startsWith('+7') ? 16 : null"
              />
            </v-col>
            <v-col cols="12" sm="6" class="edit-user__field-col">
              <v-text-field
                :value="agentDisplayName"
                label="Агент"
                outlined
                dense
                flat
                disabled
                hide-details
                prepend-inner-icon="mdi-account-tie-outline"
                class="edit-user__field"
              />
            </v-col>
            <v-col v-if="idRole === 11" cols="12" sm="6" class="edit-user__field-col">
              <v-autocomplete
                v-model="ResidentialComplexesIds"
                label="Жилой комплекс"
                :items="resResidentialComplexes"
                item-text="name"
                item-value="id"
                multiple
                outlined
                dense
                flat
                hide-details
                prepend-inner-icon="mdi-home-city-outline"
                class="edit-user__field"
                :rules="[() => !!ResidentialComplexesIds || 'Поле обязательно для заполнения']"
              />
            </v-col>
            <v-col v-if="idRole === 10" cols="12" sm="6" class="edit-user__field-col">
              <v-autocomplete
                v-model="ServiceAreaIds"
                label="Районы обслуживания"
                :items="resServiceAreas"
                item-text="name"
                item-value="id"
                multiple
                outlined
                dense
                flat
                hide-details
                prepend-inner-icon="mdi-map-marker-multiple-outline"
                class="edit-user__field"
              />
            </v-col>
            <v-col cols="12" class="edit-user__field-col edit-user__checkbox-col">
              <v-checkbox
                v-model="changePassword"
                label="Изменить пароль"
                hide-details
                class="edit-user__checkbox mt-0"
              />
            </v-col>
            <template v-if="changePassword">
              <v-col cols="12" sm="6" class="edit-user__field-col">
                <v-text-field
                  v-model="password"
                  label="Пароль"
                  type="password"
                  outlined
                  dense
                  flat
                  prepend-inner-icon="mdi-lock-outline"
                  class="edit-user__field"
                  :rules="[() => password.length > 5 || 'Пароль должен быть не менее 6 символов']"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" class="edit-user__field-col">
                <v-text-field
                  v-model="passwordConfirmation"
                  label="Подтверждение пароля"
                  type="password"
                  outlined
                  dense
                  flat
                  prepend-inner-icon="mdi-lock-check-outline"
                  class="edit-user__field"
                  :rules="[() => passwordConfirmation === this.password || 'Пароли не совпадают']"
                  required
                />
              </v-col>
            </template>
          </v-row>
          <div class="edit-user__actions">
            <v-btn
              color="primary"
              class="text-none"
              rounded
              :disabled="!valid"
              @click.prevent="updateEdit"
            >
              <v-icon left>mdi-content-save-outline</v-icon>
              Сохранить
            </v-btn>
            <v-btn
              color="primary"
              outlined
              rounded
              class="text-none ml-2"
              @click.prevent="returnUsers()"
            >
              <v-icon left>mdi-close</v-icon>
              Отменить
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import formatPhoneNumberTextField from '@/mixins/formatPhoneNumberTextField';

export default {
  name: 'EditUser',
  mixins: [formatPhoneNumberTextField],
  props: ['id'],
  data () {
    return {
      valid: true,
      model: '',
      name: '',
      email: '',
      telephone: '',
      password: '',
      idRole: '',
      agentId: '',
      changePassword: false,
      ResidentialComplexesIds: [],
      resResidentialComplexes: [],
      ServiceAreaIds: [],
      resServiceAreas: [],
      typesRole: [
        { name: 'Администратор', value: 1 },
        { name: 'Админ-инженер', value: 5 },
        { name: 'Менаджер', value: 6 },
        { name: 'Аналитик', value: 7 },
        { name: 'Кол-центр', value: 8 },
        { name: 'Мастер', value: 10 },
        { name: 'Администратор КПП', value: 11 },
      ],
      passwordConfirmation: '',
      items: [
        { text: 'Пользователи', disabled: false, href: '/users' },
        { text: 'Редактирование пользователя', disabled: true, href: '#' },
      ],
    };
  },
  async created () {
    this.loadUser(this.id)
      .then((data) => {
        this.name = data.user.name;
        this.email = data.user.email;
        this.telephone = this.formatPhoneNumberTextField(data.user.test_phone_activate || '');
        this.idRole = data.user.role.id;
        this.agentId = data.user.agent_id;
        this.ResidentialComplexesIds = data.user.residential_complexes.map(rc => rc.id);
        this.ServiceAreaIds = (data.user.service_areas || []).map(s => s.id);
      });
    const [rcData, serviceAreaData] = await Promise.all([
      this.getResidentialComplex(),
      this.getServiceAreas(),
    ]);
    this.resResidentialComplexes = rcData.data;
    this.resServiceAreas = serviceAreaData.data || [];
  },
  computed: {
    ...mapGetters({
      user: 'User/user',
      organizations: 'Agents/agents',
    }),
    agentDisplayName () {
      if (!this.agentId || !this.organizations?.length) return '';
      const org = this.organizations.find(o => o.id === this.agentId);
      return org?.user?.name ?? '';
    },
  },
  methods: {
    ...mapActions({
      loadUser: 'User/loadUser',
      updateUser: 'User/updateUser',
      getResidentialComplex: 'ResidentComplex/getComplex',
      getServiceAreas: 'ServiceArea/getServiceAreas',
    }),
    updateEdit () {
      this.$refs.form.validate();
      this.$nextTick(() => {
        if (this.valid) {
          const data = {
            id: this.$route.params.id,
            name: this.name,
            email: this.email,
            role_id: this.idRole,
          };
          if (this.changePassword && this.password) {
            data.password = this.password;
            data.password_confirmation = this.passwordConfirmation;
          }
          if (this.agentId) {
            data.agent_id = this.agentId;
          }
          if (this.ResidentialComplexesIds && this.ResidentialComplexesIds.length) {
            data.residential_complex_ids = this.ResidentialComplexesIds;
          }
          if (this.idRole === 10) {
            data.service_area_ids = this.ServiceAreaIds || [];
          }
          const testPhone = this.normalizePhoneForSave(this.telephone);
          if (testPhone !== null) {
            data.test_phone_activate = testPhone;
          }
          this.updateUser(data)
            .then(() => {
              this.returnUsers();
              this.$flash_success('Изменения сохранены');
            })
            .catch((err) => {
              if (err.message.includes('422')) {
                const response = err.response.data;
                for (const input in response) {
                  response[input].forEach(error => this.$flash_warning(input + ': ' + error));
                }
              } else {
                this.$flash_warning('Не удалось сохранить изменения');
              }
            });
        }
      });
    },
    returnUsers () {
      this.$router.push({ name: 'Users' });
    },
    normalizePhoneForSave (phone) {
      const digits = (phone || '').toString().replace(/\D/g, '');
      // Ignore empty mask-only value like "+7"
      if (digits.length <= 1) return null;
      return digits;
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-user__breadcrumbs {
  margin-bottom: 4px;
}

.edit-user__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.edit-user__header-icon {
  flex-shrink: 0;
}

.edit-user__title {
  font-size: 1.5rem;
  font-weight: 500;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.01em;
}

.edit-user__card {
  border-radius: 12px;
  max-width: 720px;
}

.edit-user__section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #546e7a;
}

.edit-user__field-col {
  padding-top: 8px;
}

.edit-user__checkbox-col {
  padding-top: 16px;
}

.edit-user__checkbox {
  margin-left: 0;
}

.edit-user__field ::v-deep .v-input__prepend-inner .v-icon {
  color: rgba(0, 0, 0, 0.5);
}

.edit-user__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 32px;
}
</style>
